import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Plus, RefreshCw, Search } from "lucide-react";
import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Pagination } from "@/components/ui/pagination";
import { EmptyState } from "@/components/EmptyState";
import { useDebounce } from "@/hooks/useDebounce";
import { formatDate } from "@/lib/utils";
import { estatusContratoEnum, type EstatusContrato } from "./schemas";
import { useListContratos } from "./api";
import { ContratoFormDialog } from "./ContratoFormDialog";
// rc97 — reintento masivo de contratos en error
import { ReintentarErroresDialog } from "./ReintentarErroresDialog";
import { useListRouters } from "@/features/routers/api";
import { useListPlanes } from "@/features/planes/api";
import { Wifi, Cable } from "lucide-react";
// rc20+: chip sub-empresa
import { EmpresaChip } from "@/features/empresas/EmpresaChip";
// rc138 — móvil: tarjetas
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

const ESTATUS_VARIANT: Record<EstatusContrato, "success" | "warning" | "destructive" | "secondary" | "default"> = {
  activo: "success",
  suspendido: "warning",
  cancelado: "destructive",
  error: "destructive",
  pendiente: "secondary",
};

export function ContratosListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 300);
  const [estatus, setEstatus] = useState<EstatusContrato | "">("");
  const [routerId, setRouterId] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  // rc97 — modal de reintento masivo de errores
  const [retryErrorsOpen, setRetryErrorsOpen] = useState(false);

  const filters = useMemo(
    () => ({ page, size: 20, search, estatus, router_id: routerId || undefined }),
    [page, search, estatus, routerId]
  );
  const { data, isLoading, isFetching } = useListContratos(filters);
  const { data: routersData } = useListRouters({ page: 1, size: 100 });
  // F108 — mapa plan_id → tipo_servicio para columna Tipo
  const { data: planesData } = useListPlanes({ page: 1, size: 200 });
  const planTipoMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of planesData?.items ?? []) m.set(p.id, p.tipo_servicio);
    return m;
  }, [planesData]);

  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Contratos</h2>
          <p className="text-xs text-muted-foreground">
            {data ? `${data.total} ${data.total === 1 ? "contrato" : "contratos"}` : "Cargando…"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* rc97 — reintento masivo de contratos en error */}
          <Button size="sm" variant="outline" onClick={() => setRetryErrorsOpen(true)} title="Reintentar errores">
            <RefreshCw size={14} /> <span className="max-md:hidden">Reintentar errores</span>
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus size={14} /> <span className="max-md:hidden">Nuevo contrato</span><span className="md:hidden">Nuevo</span>
          </Button>
        </div>
      </div>

      {/* rc138 móvil: búsqueda a lo ancho, los dos selects a la mitad */}
      <div className="flex flex-wrap items-end gap-2 max-md:[&>select]:w-auto max-md:[&>select]:min-w-0 max-md:[&>select]:flex-1">
        <div className="relative flex-1 min-w-[220px] max-md:basis-full">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
            placeholder="Buscar por código, PPPoE user o cliente…"
            className="pl-8"
          />
        </div>
        <Select
          value={estatus}
          onChange={(e) => { setEstatus(e.target.value as EstatusContrato | ""); setPage(1); }}
        >
          <option value="">Todos los estatus</option>
          {estatusContratoEnum.map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>
        <Select
          value={routerId}
          onChange={(e) => { setRouterId(e.target.value); setPage(1); }}
        >
          <option value="">Todos los routers</option>
          {routersData?.items.map((r) => (
            <option key={r.id} value={r.id}>{r.nombre}</option>
          ))}
        </Select>
      </div>

      {isMobile ? (
        <div className="flex flex-col gap-3">
          {isLoading && items.length === 0 ? (
            <MobileEmpty>Cargando contratos…</MobileEmpty>
          ) : items.length === 0 ? (
            <MobileEmpty>{search || estatus || routerId ? "Sin coincidencias con los filtros." : "Crea el primer contrato para comenzar."}</MobileEmpty>
          ) : (
            <MobileList>
              {items.map((c) => {
                const tipo = planTipoMap.get(c.plan_id);
                return (
                  <MobileCard
                    key={c.id}
                    to={`/contratos/${c.id}`}
                    title={<>{c.cliente_nombre ?? <span className="text-muted-foreground">—</span>} <EmpresaChip empresaId={c.cliente_empresa_id} /></>}
                    subtitle={
                      <>
                        <span className="font-mono">{c.codigo}</span>
                        {tipo === "wireless" && <span className="ml-1.5 inline-flex items-center gap-1 text-blue-700 dark:text-blue-300"><Wifi size={10} /> Wireless</span>}
                        {tipo === "fiber" && <span className="ml-1.5 inline-flex items-center gap-1 text-purple-700 dark:text-purple-300"><Cable size={10} /> Fibra</span>}
                      </>
                    }
                    right={<Badge variant={ESTATUS_VARIANT[c.estatus]}>{c.estatus}</Badge>}
                  >
                    <MobileMeta label="PPPoE" mono>{c.pppoe_user}</MobileMeta>
                    <MobileMeta label="IP" mono>{c.ip_asignada}</MobileMeta>
                    <MobileMeta label="Corte / equipo">Día {c.fecha_corte} · {c.equipo_modalidad === "comodato" ? "Comodato" : "Propiedad"}</MobileMeta>
                    <MobileMeta label="Activación">{c.fecha_activacion ? formatDate(c.fecha_activacion) : "—"}</MobileMeta>
                  </MobileCard>
                );
              })}
            </MobileList>
          )}
          <div className="rounded-lg border bg-card">
            <Pagination page={page} totalPages={totalPages} total={data?.total} size={data?.size} onPageChange={setPage} isFetching={isFetching} />
          </div>
        </div>
      ) : (
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Código</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>PPPoE user</TableHead>
              <TableHead>IP</TableHead>
              <TableHead>Día corte</TableHead>
              <TableHead>Estatus</TableHead>
              <TableHead>Equipo</TableHead>
              <TableHead>Activación</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando contratos…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState
                  icon={FileText}
                  title="Sin contratos"
                  description={search || estatus || routerId ? "Sin coincidencias con los filtros." : "Crea el primer contrato para comenzar."}
                  action={!search && !estatus && !routerId ? (
                    <Button size="sm" onClick={() => setCreateOpen(true)}><Plus size={14} /> Nuevo contrato</Button>
                  ) : undefined}
                />
              </TableEmpty>
            ) : (
              items.map((c) => {
                const tipo = planTipoMap.get(c.plan_id);
                return (
                <TableRow key={c.id} className="cursor-pointer" onClick={() => navigate(`/contratos/${c.id}`)}>
                  <TableCell className="font-mono">{c.codigo}</TableCell>
                  <TableCell className="max-w-[180px]" title={c.cliente_nombre ?? ""}>
                    <div className="flex flex-col gap-0.5">
                      <span className="truncate">
                        {c.cliente_nombre ?? <span className="text-muted-foreground">—</span>}
                      </span>
                      <EmpresaChip empresaId={c.cliente_empresa_id} />
                    </div>
                  </TableCell>
                  <TableCell>
                    {tipo === "wireless" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-800 ring-1 ring-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:ring-blue-900/40">
                        <Wifi size={10} /> Wireless
                      </span>
                    ) : tipo === "fiber" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-800 ring-1 ring-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-900/40">
                        <Cable size={10} /> Fibra
                      </span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono">{c.pppoe_user}</TableCell>
                  <TableCell className="font-mono text-muted-foreground">{c.ip_asignada}</TableCell>
                  {/* rc59: Día corte y fecha activación a font-mono pa unificar feel. */}
                  <TableCell className="font-mono">Día {c.fecha_corte}</TableCell>
                  <TableCell><Badge variant={ESTATUS_VARIANT[c.estatus]}>{c.estatus}</Badge></TableCell>
                  <TableCell>
                    {/* rc59: pills "Comodato"/"Propiedad" → dot+mono igual que el resto de badges. */}
                    {c.equipo_modalidad === "comodato" ? (
                      <span
                        className="inline-flex items-center gap-1.5 font-mono text-[10px] font-medium uppercase tracking-wider text-warn"
                        title="ISP recupera al cancelar"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-warn" />
                        Comodato
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1.5 font-mono text-[10px] font-medium uppercase tracking-wider text-success"
                        title="Pagado por el cliente"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-success" />
                        Propiedad
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono">{c.fecha_activacion ? formatDate(c.fecha_activacion) : "—"}</TableCell>
                </TableRow>
              );
              })
            )}
          </TableBody>
        </Table>
        {/* Paginación — compacta con números y elipsis (v1.5.0-rc2) */}
        <Pagination
          page={page}
          totalPages={totalPages}
          total={data?.total}
          size={data?.size}
          onPageChange={setPage}
          isFetching={isFetching}
        />
      </div>
      )}

      <ContratoFormDialog open={createOpen} onOpenChange={setCreateOpen} />

      {/* rc97 — montado condicional: el modal solo consulta /contratos
          estatus=error cuando está abierto */}
      {retryErrorsOpen && (
        <ReintentarErroresDialog
          open={retryErrorsOpen}
          onOpenChange={setRetryErrorsOpen}
        />
      )}
    </div>
  );
}
